import React from 'react';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Box, IconButton, Rating, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import SalonPrimaryActions from '../../../client/salonDetails/SalonPrimaryActions';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const getInitial = (name) => String(name || '').trim().charAt(0).toUpperCase() || 'G';

const GlossLabIdentity = ({
  data,
  mode = 'public',
  onBack,
  onToggleFavorite,
  onOpenMobileInfo,
  onBook,
  onContact,
  clientChatEnabled,
}) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const preview = mode === 'preview';
  const photos = Array.isArray(data?.heroPhotos) ? data.heroPhotos.filter((photo) => photo?.src) : [];
  const primaryPhoto = photos[0];
  const secondaryPhoto = photos[1];
  const hasRating = Number.isFinite(Number(data?.averageRating))
    && Number(data?.reviewCount) > 0;
  const mapsHref = data?.latitude != null && data?.longitude != null
    ? `https://www.google.com/maps?q=${data.latitude},${data.longitude}`
    : null;
  const actionProps = {
    disabled: preview,
    'aria-disabled': String(preview),
  };

  if (!data?.name) return null;

  return (
    <SalonPublicStyleScope
      sectionKey="header"
      component="header"
      data-testid="salon-public-hero"
      data-salon-template-block="identity"
      data-variant="editorial_collage"
      data-density="feature"
      sx={{
        position: 'relative',
        width: '100%',
        flexShrink: 0,
        overflow: 'hidden',
        color: theme.salon.onSecondary,
        backgroundColor: theme.salon.secondary,
        borderBottom: `8px solid ${theme.salon.primary}`,
      }}
    >
      <Typography
        aria-hidden="true"
        sx={{
          position: 'absolute',
          insetInlineStart: { xs: -18, md: '38%' },
          bottom: { xs: 8, md: -40 },
          color: 'transparent',
          fontSize: { xs: '6rem', md: '12rem' },
          fontWeight: 900,
          lineHeight: 0.8,
          letterSpacing: '-0.08em',
          opacity: 0.18,
          WebkitTextStroke: `1px ${theme.salon.onSecondary}`,
          pointerEvents: 'none',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        GLOSS
      </Typography>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 1380,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pt: { xs: 1.5, md: 2 },
          pb: { xs: 4, md: 6 },
          boxSizing: 'border-box',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <SalonPublicButton
            preserveLegacyStyle
            variant="text"
            size="small"
            startIcon={<ChevronLeftIcon />}
            onClick={onBack}
            {...actionProps}
            sx={{
              minHeight: 40,
              px: 1.5,
              color: theme.salon.onSecondary,
              backgroundColor: alpha(theme.salon.onSecondary, 0.08),
              border: `1px solid ${alpha(theme.salon.onSecondary, 0.25)}`,
              '&:hover': { backgroundColor: alpha(theme.salon.onSecondary, 0.14) },
            }}
          >
            {t('actions.back')}
          </SalonPublicButton>

          <Stack direction="row" spacing={1}>
            <IconButton
              aria-label={t('landing_salon_details.informations')}
              onClick={onOpenMobileInfo}
              {...actionProps}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                color: theme.salon.onSecondary,
                backgroundColor: alpha(theme.salon.onSecondary, 0.08),
                border: `1px solid ${alpha(theme.salon.onSecondary, 0.25)}`,
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label={t('landing_salon_details.favorite')}
              onClick={onToggleFavorite}
              {...actionProps}
              sx={{
                color: data.isFavorite ? theme.salon.primary : theme.salon.onSecondary,
                backgroundColor: alpha(theme.salon.onSecondary, 0.08),
                border: `1px solid ${alpha(theme.salon.onSecondary, 0.25)}`,
                '&:hover': { backgroundColor: alpha(theme.salon.onSecondary, 0.14) },
              }}
            >
              {data.isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
          </Stack>
        </Stack>

        <Box
          data-testid="gloss-lab-editorial-hero"
          data-layout="editorial-collage"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.9fr) minmax(420px, 1.1fr)' },
            alignItems: 'center',
            gap: { xs: 4, md: 7 },
            pt: { xs: 3.5, md: 3 },
          }}
        >
          <Stack
            alignItems={{ xs: 'center', md: 'flex-start' }}
            spacing={2}
            sx={{ minWidth: 0, textAlign: { xs: 'center', md: 'start' } }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AutoAwesomeOutlinedIcon sx={{ color: theme.salon.primary, fontSize: '1.15rem' }} />
              <Typography
                variant="overline"
                sx={{ color: theme.salon.primary, fontWeight: 800, letterSpacing: '0.16em' }}
              >
                {t('salonTemplates.glossLab.subtitle')}
              </Typography>
            </Stack>

            <Typography
              component="h1"
              data-salon-text-role="title"
              sx={{
                maxWidth: 650,
                color: theme.salon.onSecondary,
                fontSize: { xs: '2.65rem', sm: '3.5rem', md: '4.8rem' },
                fontWeight: 900,
                lineHeight: 0.94,
                letterSpacing: '-0.065em',
                textTransform: 'uppercase',
                overflowWrap: 'anywhere',
              }}
            >
              {data.name}
            </Typography>

            {hasRating ? (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  width: 'fit-content',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  color: theme.salon.secondary,
                  backgroundColor: theme.salon.primary,
                }}
              >
                <Rating
                  value={Number(data.averageRating)}
                  precision={0.1}
                  readOnly
                  size="small"
                  aria-label={t('landing_salon_details.reviews_count', { count: data.reviewCount })}
                  sx={{ '& .MuiRating-icon': { color: theme.salon.secondary } }}
                />
                <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 900 }}>
                  {Number(data.averageRating).toFixed(1)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 700 }}>
                  {t('landing_salon_details.reviews_count_short', { count: data.reviewCount })}
                </Typography>
              </Stack>
            ) : null}

            {data.description ? (
              <Typography
                data-salon-text-role="content"
                sx={{
                  maxWidth: 560,
                  color: alpha(theme.salon.onSecondary, 0.7),
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  lineHeight: 1.7,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {data.description}
              </Typography>
            ) : null}

            <Stack
              data-salon-text-role="content"
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 2.5 }}
              alignItems={{ xs: 'center', sm: 'flex-start' }}
              sx={{ color: alpha(theme.salon.onSecondary, 0.72) }}
            >
              {data.address ? (
                <Stack
                  component={mapsHref ? 'a' : 'div'}
                  href={!preview ? mapsHref : undefined}
                  target={mapsHref && !preview ? '_blank' : undefined}
                  rel={mapsHref && !preview ? 'noopener noreferrer' : undefined}
                  direction="row"
                  spacing={0.75}
                  alignItems="flex-start"
                  sx={{ maxWidth: 310, color: 'inherit', textDecoration: 'none' }}
                >
                  <LocationOnOutlinedIcon sx={{ mt: 0.1, color: theme.salon.primary, fontSize: '1rem' }} />
                  <Typography variant="body2" sx={{ color: 'inherit' }}>{data.address}</Typography>
                </Stack>
              ) : null}
              {data.phone ? (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'inherit' }}>
                  <PhoneOutlinedIcon sx={{ color: theme.salon.primary, fontSize: '1rem' }} />
                  <Typography variant="body2" sx={{ color: 'inherit' }}>{data.phone}</Typography>
                </Stack>
              ) : null}
            </Stack>

            {!preview && typeof onBook === 'function' ? (
              <SalonPrimaryActions
                inline
                clientChatEnabled={clientChatEnabled}
                onBook={onBook}
                onContact={onContact}
                bookLabel={t('landing_salon_details.book_appointment')}
              />
            ) : null}
          </Stack>

          <Box
            data-testid="gloss-lab-photo-collage"
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 620,
              height: { xs: 330, sm: 430, md: 475 },
              mx: 'auto',
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                top: { xs: 20, md: 5 },
                right: { xs: '3%', md: '7%' },
                width: { xs: 190, sm: 280, md: 330 },
                height: { xs: 190, sm: 280, md: 330 },
                borderRadius: '50%',
                backgroundColor: theme.salon.primary,
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                top: { xs: 5, md: 0 },
                right: { xs: '10%', sm: '12%', md: '11%' },
                width: { xs: '66%', sm: '64%', md: '62%' },
                height: { xs: 285, sm: 385, md: 430 },
                overflow: 'hidden',
                borderRadius: { xs: '80px 20px 80px 20px', md: '140px 28px 140px 28px' },
                border: `7px solid ${theme.salon.onSecondary}`,
                backgroundColor: alpha(theme.salon.onSecondary, 0.08),
                transform: 'rotate(2.5deg)',
                boxShadow: `0 24px 60px ${alpha('#000000', 0.36)}`,
              }}
            >
              {primaryPhoto ? (
                <Box
                  component="img"
                  src={primaryPhoto.src}
                  alt={primaryPhoto.alt || data.name}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.03)' }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: '100%',
                    height: '100%',
                    color: theme.salon.secondary,
                    backgroundColor: theme.salon.primary,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '6rem', md: '9rem' }, fontWeight: 900 }}>
                    {getInitial(data.name)}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                position: 'absolute',
                left: { xs: '2%', sm: '5%', md: '2%' },
                bottom: { xs: 0, sm: 5, md: 0 },
                display: 'grid',
                placeItems: 'center',
                width: { xs: 130, sm: 175, md: 195 },
                height: { xs: 145, sm: 195, md: 220 },
                overflow: 'hidden',
                color: theme.salon.secondary,
                backgroundColor: theme.salon.primary,
                border: `7px solid ${theme.salon.secondary}`,
                borderRadius: '24px 70px 24px 24px',
                transform: 'rotate(-6deg)',
                boxShadow: `0 18px 40px ${alpha('#000000', 0.34)}`,
              }}
            >
              {secondaryPhoto ? (
                <Box
                  component="img"
                  src={secondaryPhoto.src}
                  alt={secondaryPhoto.alt || ''}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <AutoAwesomeOutlinedIcon sx={{ fontSize: { xs: '3rem', md: '4rem' } }} />
              )}
            </Box>

            <Typography
              aria-hidden="true"
              sx={{
                position: 'absolute',
                right: { xs: -18, md: -8 },
                top: '42%',
                color: theme.salon.onSecondary,
                fontSize: { xs: '0.65rem', md: '0.75rem' },
                fontWeight: 900,
                letterSpacing: '0.28em',
                writingMode: 'vertical-rl',
                textTransform: 'uppercase',
              }}
            >
              Beauty culture / Gloss lab
            </Typography>
          </Box>
        </Box>
      </Box>
    </SalonPublicStyleScope>
  );
};

export default GlossLabIdentity;
